import { useEffect, useRef, useState } from 'preact/hooks';
import type { AppRole } from '../../shared/roles';
import { useI18n } from '../i18n';
import { attentionFor, totalOf, type Alert } from '../lib/attention';

// The header bell (D91): how many things wait on you, and where. Live counts, refreshed when
// the app opens, every 5 minutes, when the window comes back into focus and after moving page
// (at most every 30 seconds). Disclosure pattern like the account menu.

const EVERY = 5 * 60_000;
const MIN_GAP = 30_000;

export function Bell({
  roles,
  poApprover = false,
  path,
}: {
  roles: readonly AppRole[];
  poApprover?: boolean;
  path: string;
}) {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const last = useRef(0);
  const key = `${roles.join(',')}|${String(poApprover)}`;

  const refresh = (force = false) => {
    const now = Date.now();
    if (!force && now - last.current < MIN_GAP) return;
    last.current = now;
    void attentionFor(roles, poApprover).then(setAlerts);
  };

  useEffect(() => {
    refresh(true);
    const timer = setInterval(() => refresh(true), EVERY);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [key]);

  useEffect(() => {
    setOpen(false);
    refresh();
  }, [path]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  const total = alerts ? totalOf(alerts) : 0;
  const urgent = alerts?.some((a) => a.urgent) ?? false;

  return (
    <div class="account bell" ref={root}>
      <button
        ref={button}
        type="button"
        class="lang-switch bell__button"
        aria-expanded={open}
        aria-controls="bell-panel"
        aria-label={total ? t('bell.labelCount', { n: String(total) }) : t('bell.label')}
        onClick={() => {
          if (!open) refresh();
          setOpen(!open);
        }}
      >
        <svg
          aria-hidden="true"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {total > 0 && (
          <span class={`bell__count${urgent ? ' bell__count--urgent' : ''}`} aria-hidden="true">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
      <div id="bell-panel" class="account__panel bell__panel" hidden={!open}>
        <p class="account__name">{t('bell.title')}</p>
        {alerts === null ? (
          <p class="caption bell__empty">{t('req.loading')}</p>
        ) : alerts.length === 0 ? (
          <p class="caption bell__empty">{t('bell.none')}</p>
        ) : (
          <ul class="account__list bell__list">
            {alerts.map((a) => (
              <li key={a.key}>
                <a href={a.href} class={a.urgent ? 'bell__item bell__item--urgent' : 'bell__item'}>
                  <span>{t(a.key)}</span>
                  <span class="bell__n" dir="ltr">
                    {a.count}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
